/**
 * Entrar — DESKTOP: dos columnas. El formulario a la derecha y a la izquierda lo que la app
 * responde, que es la pregunta que la distingue: no "cuánto bajé" sino "lo que bajé fue grasa".
 *
 * En el celular esa columna no existe: quien abre la app en el gimnasio ya sabe qué es y sólo
 * quiere entrar.
 */

import type { ReactNode } from 'react';

import { useEntrar } from '../features/useEntrar.ts';
import { MINIMO_CLAVE } from '../features/useClave.ts';
import { Aviso, Campo, Pila, estadosDeRatio } from '../ui/componentes.tsx';

export function EntrarDesktop({ onEntro }: { onEntro: () => void }): ReactNode {
  const f = useEntrar(onEntro);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', minHeight: '100%' }}>
      <section
        style={{
          padding: 'var(--e7)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 'var(--e5)',
          borderRight: '1px solid var(--hierro-borde)',
          background: 'var(--hierro-hundido)',
        }}
      >
        <div>
          <span className="eyebrow">La pregunta que responde</span>
          <h1
            style={{
              fontSize: 'clamp(30px, 3.4vw, 46px)',
              lineHeight: 1.08,
              letterSpacing: '-0.035em',
              fontWeight: 650,
              marginTop: 'var(--e3)',
              maxWidth: '20ch',
            }}
          >
            No cuánto bajaste. Cuánto de lo que bajaste era grasa.
          </h1>
        </div>

        <div style={{ maxWidth: 460 }}>
          <div className="fila fila--entre" style={{ marginBottom: 8 }}>
            <span className="eyebrow">Calidad de la pérdida</span>
            <span className="lectura apagado" style={{ fontSize: 13 }}>
              ejemplo
            </span>
          </div>
          <Pila estados={estadosDeRatio(0.85, 20)} alto={38} etiqueta="Ejemplo: 85 % de la pérdida fue grasa" />
          <p className="nota" style={{ marginTop: 12 }}>
            Cada placa es un 5 % de lo que bajaste. Las llenas son grasa; las huecas, masa magra
            que entregaste. Un gráfico de peso a secas no distingue las dos cosas, y el día que
            ganás medio kilo de músculo te dice que retrocediste.
          </p>
        </div>

        <ul className="nota" style={{ margin: 0, paddingLeft: '1.1em', display: 'grid', gap: 6 }}>
          <li>Cada serie se guarda al tocarla, con o sin señal.</li>
          <li>Sabe si te alcanzan los días del plan antes de que venza.</li>
          <li>Trata las lecturas de la balanza como estimaciones, no como hechos.</li>
        </ul>
      </section>

      <section style={{ display: 'grid', placeItems: 'center', padding: 'var(--e6)' }}>
        <form
          className="ancho-forma"
          onSubmit={(e) => {
            e.preventDefault();
            void f.enviar();
          }}
        >
          <div>
            {/* El título dice cuál de las dos cosas está pasando: los dos formularios son casi
                idénticos y sin esto sólo se distinguen por el texto del botón. */}
            <h2 className="titulo">{f.titulo}</h2>
            <p className="nota" style={{ marginTop: 4 }}>
              {f.modo === 'crear'
                ? 'Se hace una sola vez. Después la app funciona con o sin señal.'
                : 'Con el email y la clave que usaste al crear la cuenta.'}
            </p>
          </div>

          {f.modo === 'crear' && (
            <Campo
              etiqueta="Nombre"
              texto
              autoComplete="name"
              value={f.nombre}
              onChange={(e) => f.setNombre(e.target.value)}
            />
          )}

          <Campo
            etiqueta="Email"
            texto
            type="email"
            autoComplete="email"
            value={f.email}
            onChange={(e) => f.setEmail(e.target.value)}
          />

          <Campo
            etiqueta="Clave"
            texto
            type="password"
            autoComplete={f.modo === 'crear' ? 'new-password' : 'current-password'}
            value={f.clave}
            onChange={(e) => f.setClave(e.target.value)}
            {...(f.modo === 'crear' ? { ayuda: `Mínimo ${MINIMO_CLAVE} caracteres.` } : {})}
          />

          {f.error && <Aviso tono="importante">{f.error}</Aviso>}

          {/* "Email o clave incorrectos" es lo mismo que ve alguien que nunca creó la cuenta. Acá,
              debajo del error y no al pie, está la única salida que le sirve. */}
          {f.sinCuenta && (
            <button type="button" className="boton" onClick={() => f.cambiarModo('crear')}>
              ¿Primera vez? Creá tu cuenta
            </button>
          )}

          <button type="submit" className="boton boton--principal" disabled={!f.valido || f.enviando}>
            {f.enviando ? 'Un momento…' : f.titulo}
          </button>

          {/* El botón arranca deshabilitado con el formulario vacío: sin esta línea es una pared. */}
          {f.queFalta && !f.enviando && (
            <p className="nota--fina" style={{ marginTop: -8 }}>
              {f.queFalta}
            </p>
          )}

          {!f.sinCuenta && (
            <button
              type="button"
              className="boton boton--fantasma boton--chico"
              onClick={() => f.cambiarModo(f.modo === 'crear' ? 'entrar' : 'crear')}
            >
              {f.modo === 'crear' ? 'Ya tengo cuenta' : 'Crear una cuenta'}
            </button>
          )}

          {f.modo === 'crear' && (
            <p className="nota--fina">
              Tus datos van a una planilla propia, separada de la de cualquier otra persona.
            </p>
          )}
        </form>
      </section>
    </div>
  );
}
